<template>
  <div>
    <div class="expiry-data d-flex justify-content-between align-items-center flex-wrap mb-3">
      <div class="d-flex align-items-center p-0">
        <h2 class="text-left mb-0">{{ unit.label }} <span>({{ form.name }})</span></h2>
      </div>
      <p class="f-14 text-grey mb-0">
        {{ $t("dvir.itemsCompleted", { done: completedCount, total: items.length }) }}
      </p>
    </div>

    <div
      class="inspection-card mb-2"
      v-for="(item, index) in items"
      :key="item.id"
      :class="{ 'is-locked': !canOpen(index), 'is-open': isOpen(index) }"
    >
      <div class="inspection-head d-flex align-items-center" @click="toggle(index)">
        <div class="status-dot mr-3" :class="statusClass(index)">
          <b-icon v-if="isComplete(index)" icon="check" aria-hidden="true"></b-icon>
          <b-icon v-else-if="!canOpen(index)" icon="lock-fill" aria-hidden="true"></b-icon>
          <span v-else>{{ index + 1 }}</span>
        </div>
        <div class="flex-grow-1 text-left">
          <div class="f-14 font-weight-bold text-black">{{ item.name }}</div>
          <div class="f-12 text-grey" v-if="item.description">{{ item.description }}</div>
        </div>
        <span class="f-12 text-grey mr-3 text-nowrap">{{ index + 1 }}/{{ items.length }}</span>
        <b-icon :icon="isOpen(index) ? 'chevron-up' : 'chevron-down'" class="text-grey"></b-icon>
      </div>
      <b-collapse :visible="isOpen(index)">
        <div class="inspection-body text-left px-0" v-if="answers[index]">
          <!-- Pass / Fail buttons — every type except a note-only item. -->
          <div v-if="hasPassFail(item)" class="px-0 btn-split btn-split--half">
            <b-button
              class="ans-btn"
              :variant="answers[index].answer === 'pass' ? 'primary' : 'outline-primary'"
              @click="setAnswer(index, 'pass')"
            >
              {{ $t("dvir.pass") }}
            </b-button>
            <b-button
              class="ans-btn"
              :variant="answers[index].answer === 'fail' ? 'secondary' : 'outline-secondary'"
              @click="setAnswer(index, 'fail')"
            >
              {{ $t("dvir.fail") }}
            </b-button>
          </div>

          <!-- Photos: always for a photo item, and on any answer for
               pass-fail-photo. A plain pass/fail item has none. -->
          <div v-if="showPhotos(index)" :class="{ 'mt-3': hasPassFail(item) }">
            <label class="state d-block">
              {{ photoRequired(index) ? $t("dvir.photoRequired") : $t("dvir.photoOptional") }}
            </label>
            <!-- One photo per item: a full-width preview with the count and a
                 delete chip, or the capture controls when empty. -->
            <div class="photo-preview" v-if="answers[index].photos.length">
              <img :src="answers[index].photos[0].url" alt="" />
              <span class="photo-count">1/1</span>
              <span class="photo-delete" @click="removePhoto(index, 0)">
                <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 5.15704H17.6139" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path
                    d="M3.0766 5.15704H15.537V17.6175C15.537 17.9846 15.3911 18.3368 15.1315 18.5964C14.8719 18.856 14.5197 19.0019 14.1525 19.0019H4.46109C4.0939 19.0019 3.74175 18.856 3.4821 18.5964C3.22247 18.3368 3.0766 17.9846 3.0766 17.6175V5.15704Z"
                    stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M5.84607 5.15347V4.46123C5.84607 3.54325 6.21073 2.66287 6.85984 2.01377C7.50894 1.36466 8.38932 1 9.3073 1C10.2253 1 11.1057 1.36466 11.7548 2.01377C12.4039 2.66287 12.7685 3.54325 12.7685 4.46123V5.15347"
                    stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M7.23175 9.3065V14.8466" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                  <path d="M11.385 9.3065V14.8466" stroke="#2D69F6" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </span>
            </div>
            <div class="d-flex align-items-center flex-wrap" v-else>
              <b-button
                variant="outline-primary"
                class="mx-0 h-40px px-3 mr-2 d-flex align-items-center f-14"
                @click="openCamera(index)"
              >
                <b-icon icon="camera" class="p-0 mr-2 fill-primary" aria-hidden="true"></b-icon>
                {{ $t("dvir.takePhoto") }}
              </b-button>
              <!-- `capture` asks a phone for the camera; on desktop it is ignored
                   and this stays a plain file picker. -->
              <!-- <label class="photo-upload mb-0">
                <b-icon icon="plus" class="mr-2"></b-icon>
                {{ $t("dvir.uploadPhoto") }}
                <input type="file" accept="image/*" capture="environment" @change="onPhotoChange(index, $event)" />
              </label> -->
            </div>
          </div>

          <!-- Note: required for a note item, optional on the photo types. -->
          <div v-if="showNote(index)" :class="{ 'mt-3': hasPassFail(item) || showPhotos(index) }">
            <label class="state d-block">
              {{ item.card_type === "note" ? $t("dvir.noteLabel") : $t("dvir.noteOptional") }}
            </label>
            <b-form-textarea
              v-model="answers[index].note"
              class="f-12"
              :placeholder="$t('dvir.notePlaceholder')"
              rows="3"
            ></b-form-textarea>
          </div>

          <div class="d-flex mt-3" v-if="showOkButton(index)">
            <b-button
              variant="primary"
              class="mx-0 mb-0 h-40px w-100px m-0"
              @click="confirmItem(index)"
            >
              {{ $t("dvir.ok") }}
            </b-button>
          </div>
        </div>
      </b-collapse>
    </div>

    <div class="setting-card mt-3" @click="notifyIfLocked">
      <h4 class="mb-0 pl-0 text-left">{{ $t("dvir.inspectionDetails") }}</h4>
      <b-row class="px-0">
        <b-col md="6" class="pr-3 pr-md-0">
          <b-form-group :label="$t('dvir.odometer')" class="text-left mb-0">
            <b-form-input
              v-model="details.odometer"
              type="text"
              class="unit"
              v-mask="numberMask"
              :disabled="!allItemsConfirmed"
              :placeholder="$t('dvir.odometerPlaceholder')"
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6" class="mt-3 mt-md-0">
          <b-form-group :label="$t('dvir.location')" class="text-left mb-0">
            <b-form-input
              v-model="details.location"
              type="text"
              class="unit"
              :disabled="!allItemsConfirmed"
              :placeholder="$t('dvir.locationPlaceholder')"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <div class="setting-card mt-3" @click="notifyIfLocked">
      <h4 class="mb-0 pl-0 text-left">{{ $t("dvir.status") }}</h4>
      <div class="btn-split btn-split--half px-0">
        <b-button
          v-for="option in statusOptions"
          :key="option.value"
          class="ans-btn"
          :variant="overallStatus === option.value ? option.variant : `outline-${option.variant}`"
          :disabled="!allItemsConfirmed"
          @click="overallStatus = option.value"
        >
          {{ option.label }}
        </b-button>
      </div>
    </div>
    <div class="setting-card mt-3" @click="notifyIfLocked">
      <h4 class="mb-0 pl-0 text-left">
        {{ $t("dvir.driverSignature") }}
        {{ requiresDriverSignature ? $t("dvir.requiredLabel") : $t("dvir.optionalLabel") }}
      </h4>
      <SignaturePad v-model="signatures.driver" :disabled="!allItemsConfirmed" />
      <div class="d-flex justify-content-end align-items-center flex-wrap mt-4 p-0">
        <b-button
          variant="secondary"
          class="mx-0 h-40px w-100px mr-2 d-flex justify-content-center align-items-center f-14"
          @click.stop="$emit('close')"
        >
          {{ $t("maintenanceShared.close") }}
        </b-button>
        <b-button
          variant="primary"
          class="export equipment d-flex justify-content-center align-items-center px-3 h-40px"
          @click.stop="submit"
        >
          <p class="mb-0 text-white p-0">{{ $t("dvir.complete") }}</p>
        </b-button>
      </div>
    </div>
    <!-- Only asked for when the template wants it and something actually failed. -->
    <!-- <div class="setting-card mt-3" v-if="mechanicSignatureRequired" @click="notifyIfLocked">
      <h4 class="mb-0 pl-0 text-left">
        {{ $t("dvir.mechanicSignature") }}
        {{ $t("dvir.requiredLabel") }}
      </h4>
      <SignaturePad v-model="signatures.mechanic" :disabled="!allItemsConfirmed" />
       <div class="d-flex justify-content-end align-items-center flex-wrap mt-4 p-0">
        <b-button
          variant="secondary"
          class="mx-0 h-40px w-100px mr-2 d-flex justify-content-center align-items-center f-14"
          @click.stop="$emit('close')"
        >
          {{ $t("maintenanceShared.close") }}
        </b-button>
        <b-button
          variant="primary"
          class="export equipment d-flex justify-content-center align-items-center px-3 h-40px"
          @click.stop="submit"
        >
          <p class="mb-0 text-white p-0">{{ $t("dvir.complete") }}</p>
        </b-button>
      </div>
    </div> -->

    <b-modal
      id="modal-dvir-camera"
      centered
      no-close-on-backdrop
      no-close-on-esc
      :footer-class="'d-block border-0 pt-0 pb-3 m-0'"
      :body-class="'px-3 pt-2'"
      :header-class="'align-items-center'"
      @shown="startCamera"
      @hidden="stopCamera"
    >
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t("dvir.takePhoto") }}
        </h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>
      </template>
      <div class="camera-frame">
        <video ref="cameraVideo" autoplay playsinline muted></video>
      </div>
      <p v-if="cameraError" class="field-error mt-2 mb-0">{{ cameraError }}</p>
      <template #modal-footer>
        <div class="text-right modal-footer border-0 p-0 mb-0 mt-0">
          <b-button class="mx-0 h-40px w-100px m-0 mr-3" variant="secondary" @click="closeCamera">
            {{ $t("dvir.cancel") }}
          </b-button>
          <b-button
            class="mx-0 mb-0 h-40px px-3 m-0"
            variant="primary"
            :disabled="!!cameraError"
            @click="capturePhoto"
          >
            {{ $t("dvir.capture") }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import SignaturePad from "@/components/Maintenance/SignaturePad.vue";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
const numberMask = createNumberMask({
    prefix: "",
    allowDecimal: true,
    includeThousandsSeparator: true,
});
export default {
  name: "AddDVIRForm",
  components: { SignaturePad },
  props: {
    unit: {
      type: Object,
      default: null,
    },
    template: {
      type: Object,
      default: null,
    },
    // The record created when the unit + template were picked; its id is what
    // the filled-in answers get saved against.
    inspection: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      numberMask,
      // More than one card can be open: the one being worked on, plus every
      // failure, which stays expanded.
      openIndexes: [0],
      answers: [],
      inspectionData: null,
      signatures: { driver: "", mechanic: "" },
      details: { odometer: "", location: "" },
      overallStatus: "",
      cameraIndex: null,
      cameraStream: null,
      cameraError: "",
    };
  },
  computed: {
    record() {
      return this.inspectionData || this.inspection || {};
    },
    form() {
      return this.record.template_detail || this.template || {};
    },
    // Only the signatures the template asks for are collected.
    requiresDriverSignature() {
      return this.form.require_driver_signature === true;
    },
    requiresMechanicSignature() {
      return this.form.require_mechanic_signature === true;
    },
    hasDefects() {
      return this.answers.some((answer) => answer.answer === "fail");
    },
    // The template asks for a mechanic signature, but only a DVIR with defects
    // actually has to carry one.
    mechanicSignatureRequired() {
      return this.requiresMechanicSignature && this.hasDefects;
    },
    items() {
      // An inspection carries its own item list; a bare template only has a definition.
      const items = this.record.items || (this.template && this.template.definition) || [];
      return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    completedCount() {
      return this.items.filter((item, index) => this.isConfirmed(index)).length;
    },
    // -1 once every item has been confirmed with its Ok button.
    firstUnconfirmed() {
      return this.items.findIndex((item, index) => !this.isConfirmed(index));
    },
    // These are the safety_status values the API stores, so they go up as-is.
    statusOptions() {
      return [
        { value: "Safe", label: this.$t("dvir.statusSafe"), variant: "primary" },
        { value: "Unsafe", label: this.$t("dvir.statusUnsafe"), variant: "secondary" },
        // { value: "Resolved", label: this.$t("dvir.statusResolved"), variant: "success" },
      ];
    },
    // The outcome and signature only unlock once every item is done.
    allItemsConfirmed() {
      return this.items.length > 0 && this.firstUnconfirmed === -1;
    },
  },
  methods: {
    ...mapActions([
      "getDVIRInspection",
      "updateDVIRInspection",
      "addDVIRInspectionMedia",
      "setNotification",
      "setLoader",
    ]),
    async loadInspection() {
      if (!this.inspection || !this.inspection.id) {
        this.resetAnswers();
        return;
      }
      try {
        this.setLoader(true);
        this.inspectionData = await this.getDVIRInspection(this.inspection.id);
      } catch (error) {
        console.error("Error fetching DVIR inspection:", error);
      } finally {
        // Answers are keyed by position, so rebuild them against whatever
        // item list we ended up with.
        this.resetAnswers();
        this.setLoader(false);
      }
    },
    photoKey(url) {
      try {
        return new URL(url).pathname;
      } catch (error) {
        return url;
      }
    },
    resetAnswers() {
      this.details = {
        odometer: this.record.odometer || "",
        location: this.record.location || "",
      };
      this.overallStatus = this.record.safety_status || "";
      const signatures = this.record.signatures || {};
      this.signatures = { driver: signatures.driver || "", mechanic: signatures.mechanic || "" };
      this.answers = this.items.map((item) => {
        // Photos already on the record are urls; ones added here also carry a File.
        const seen = new Set();
        const photos = (item.photos || [])
          .map((photo) => ({
            file: null,
            url: typeof photo === "string" ? photo : photo.url,
          }))
          .filter((photo) => {
            // A presigned url re-signs the same object with a new query string,
            // so compare the path to spot repeats.
            const key = this.photoKey(photo.url);
            if (!photo.url || seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 1);
        const answer = {
          answer: item.answer || "",
          note: item.note || "",
          photos,
          confirmed: false,
        };
        return answer;
      });
      // Anything already filled in counts as confirmed, so reopening a DVIR
      // doesn't re-lock every card behind the first one.
      this.answers.forEach((answer, index) => {
        answer.confirmed = this.isComplete(index);
      });
      // Everything already answered stays expanded, along with wherever the
      // DVIR was left off — a fresh one therefore opens on the first item only.
      const answered = this.answers.reduce(
        (open, answer, index) => (answer.confirmed ? [...open, index] : open),
        []
      );
      this.openIndexes = [...new Set([...answered, Math.max(this.firstUnconfirmed, 0)])];
    },
    hasPassFail(item) {
      return item.card_type === "pass-fail" || item.card_type === "pass-fail-photo";
    },
    showPhotos(index) {
      const item = this.items[index];
      if (!item || !this.answers[index]) return false;
      if (item.card_type === "photo") return true;
      // pass-fail-photo only asks for a photo on a failure; a plain pass/fail
      // item never does.
      return item.card_type === "pass-fail-photo" && this.answers[index].answer === "fail";
    },
    photoRequired(index) {
      const item = this.items[index];
      if (!item || !this.answers[index]) return false;
      if (item.card_type === "photo") return true;
      // A pass/fail + photo item only has to be evidenced when it failed.
      return item.card_type === "pass-fail-photo" && this.answers[index].answer === "fail";
    },
    showNote(index) {
      const item = this.items[index];
      if (!item || !this.answers[index]) return false;
      // A plain pass/fail item is just the two buttons — no photo, no note.
      if (item.card_type === "note" || item.card_type === "photo") return true;
      // pass-fail-photo asks for a note only on a failure, alongside the photo.
      return item.card_type === "pass-fail-photo" && this.answers[index].answer === "fail";
    },
    isComplete(index) {
      const item = this.items[index];
      const answer = this.answers[index];
      if (!item || !answer) return false;
      switch (item.card_type) {
        case "pass-fail":
          return !!answer.answer;
        case "pass-fail-photo":
          if (!answer.answer) return false;
          return answer.answer === "pass" || answer.photos.length > 0;
        case "photo":
          return answer.photos.length > 0;
        case "note":
          return !!answer.note.trim();
        default:
          return !!answer.answer;
      }
    },
    // An item counts as done only once Ok was pressed — and it drops back if the
    // answer is later cleared.
    isConfirmed(index) {
      const answer = this.answers[index];
      return !!answer && answer.confirmed && this.isComplete(index);
    },
    // Everything up to and including the first unconfirmed item is reachable.
    canOpen(index) {
      const first = this.firstUnconfirmed;
      return first === -1 || index <= first;
    },
    statusClass(index) {
      if (this.isConfirmed(index)) return "is-done";
      if (!this.canOpen(index)) return "is-locked";
      return "is-current";
    },
    toggle(index) {
      if (!this.canOpen(index)) {
        this.setNotification({
          msg: this.$t("dvir.completePreviousItem"),
          type: "error",
          color: "red",
        });
        return;
      }
      this.openIndexes = this.isOpen(index)
        ? this.openIndexes.filter((open) => open !== index)
        : [...this.openIndexes, index];
    },
    notifyIfLocked() {
      if (this.allItemsConfirmed) return;
      this.setNotification({
        msg: this.$t("dvir.completeAllItems"),
        type: "error",
        color: "red",
      });
    },
    async confirmItem(index) {
      if (!this.isComplete(index)) {
        await this.setNotification({
          msg: this.$t("dvir.completeItem"),
          type: "error",
          color: "red",
        });
        return;
      }
      this.$set(this.answers, index, { ...this.answers[index], confirmed: true });
      // Saved as we go so a half-finished DVIR isn't lost — no loader, since it
      // runs on every step and would blink over the whole page.
      try {
        await this.uploadPhotos(this.record.id, index);
        await this.patchInspection(false);
      } catch (error) {
        this.$set(this.answers, index, { ...this.answers[index], confirmed: false });
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
        return;
      }
      // Move on to the next item, leaving this one open so the driver can still
      // see what was answered.
      const next = index + 1 < this.items.length ? [index + 1] : [index];
      this.openIndexes = [...new Set([...this.openIndexes, index, ...next])];
    },
    isOpen(index) {
      // A failure is always expanded — on load, after a save, and the moment it
      // is marked — so its photo and note are never hidden away.
      const answer = this.answers[index];
      if (answer && answer.answer === "fail") return true;
      return this.openIndexes.includes(index);
    },
    // True when the answer alone finishes the item, so there is nothing left to
    // fill in and no Ok button to press.
    answerConfirms(index) {
      const item = this.items[index];
      const answer = this.answers[index];
      if (!item || !answer) return false;
      if (item.card_type === "pass-fail") return true;
      // A passing photo item asks for nothing else; a failing one wants a photo.
      return item.card_type === "pass-fail-photo" && answer.answer === "pass";
    },
    // Ok only appears when there is something to confirm beyond the answer: a
    // failed photo item, or a photo/note item that has no answer at all.
    showOkButton(index) {
      const item = this.items[index];
      const answer = this.answers[index];
      if (!item || !answer) return false;
      if (item.card_type === "pass-fail") return false;
      if (item.card_type === "pass-fail-photo") return answer.answer === "fail";
      return true;
    },
    async setAnswer(index, value) {
      const answer = this.answers[index];
      this.$set(this.answers, index, { ...answer, answer: value });
      // Picking the answer is the confirmation when nothing else is needed.
      if (this.answerConfirms(index)) {
        await this.confirmItem(index);
      }
    },
    openCamera(index) {
      this.cameraIndex = index;
      this.cameraError = "";
      this.$bvModal.show("modal-dvir-camera");
    },
    closeCamera() {
      this.$bvModal.hide("modal-dvir-camera");
    },
    async startCamera() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = this.$t("dvir.cameraUnavailable");
        return;
      }
      try {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (this.$refs.cameraVideo) this.$refs.cameraVideo.srcObject = this.cameraStream;
      } catch (error) {
        console.error("Camera error:", error);
        this.cameraError = this.$t("dvir.cameraUnavailable");
      }
    },
    stopCamera() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach((track) => track.stop());
        this.cameraStream = null;
      }
      if (this.$refs.cameraVideo) this.$refs.cameraVideo.srcObject = null;
    },
    capturePhoto() {
      const video = this.$refs.cameraVideo;
      if (!video || !video.videoWidth) return;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const file = new File([blob], `dvir-${Date.now()}.jpg`, { type: "image/jpeg" });
        this.setPhoto(this.cameraIndex, file);
        this.closeCamera();
      }, "image/jpeg", 0.9);
    },
    setPhoto(index, file) {
      // One photo per item — a new shot replaces whatever was there.
      this.answers[index].photos.forEach((photo) => {
        if (photo.file) URL.revokeObjectURL(photo.url);
      });
      const photos = [{ file, url: URL.createObjectURL(file) }];
      this.$set(this.answers, index, { ...this.answers[index], photos });
    },
    onPhotoChange(index, event) {
      const file = (event.target.files || [])[0];
      if (!file) return;
      this.setPhoto(index, file);
      // Let the same file be picked again after removing it.
      event.target.value = "";
    },
    removePhoto(index, photoIndex) {
      const photos = [...this.answers[index].photos];
      const [removed] = photos.splice(photoIndex, 1);
      // Only locally captured photos hold an object url worth revoking.
      if (removed && removed.file) URL.revokeObjectURL(removed.url);
      this.$set(this.answers, index, { ...this.answers[index], photos });
    },
    async uploadPhotos(inspectionID, index) {
      // Each photo is posted on its own and comes back with the S3 url the PATCH
      // references. Uploaded photos lose their File so a re-save can't duplicate them.
      const item = this.items[index];
      const photos = this.answers[index].photos;
      const uploaded = [];
      for (const photo of photos) {
        if (!photo.file) {
          uploaded.push({ file: null, url: photo.url });
          continue;
        }
        const payload = new FormData();
        payload.append("file", photo.file);
        payload.append("item_ref", `${item.id}`);
        payload.append("item_name", item.name);
        payload.append("media_type", "image");
        const media = await this.addDVIRInspectionMedia({ id: inspectionID, payload });
        URL.revokeObjectURL(photo.url);
        uploaded.push({ file: null, url: media && media.url ? media.url : photo.url });
      }
      this.$set(this.answers, index, { ...this.answers[index], photos: uploaded });
    },
    // Every answered item that has been confirmed, in the shape the API expects.
    resultItems() {
      return this.items
        .map((item, index) => ({ item, answer: this.answers[index], index }))
        .filter(({ index }) => this.isConfirmed(index))
        .map(({ item, answer }) => {
          // Photos are deliberately left out: the API merges the uploaded media
          // into the item by item_ref, so echoing the urls back duplicates them.
          const entry = { id: item.id, answer: answer.answer || null };
          if (answer.note.trim()) entry.note = answer.note.trim();
          return entry;
        });
    },
    async patchInspection(completed) {
      const safetyStatus = this.overallStatus;
      const result = { items: this.resultItems() };
      // Only stamp the outcome once it has actually been chosen.
      if (this.overallStatus) result.safety_status = safetyStatus;
      if (completed) {
        const signatures = {};
        if (this.signatures.driver) signatures.driver = this.signatures.driver;
        if (this.signatures.mechanic) signatures.mechanic = this.signatures.mechanic;
        result.signatures = signatures;
      }
      const payload = {
        result,
        odometer: this.details.odometer,
        location: this.details.location,
      };
      if (this.overallStatus) payload.safety_status = safetyStatus;
      if (completed) payload.progress_status = "Completed";
      await this.updateDVIRInspection({ id: this.record.id, payload });
    },
    async submit() {
      if (this.firstUnconfirmed !== -1) {
        this.openIndexes = [...new Set([...this.openIndexes, this.firstUnconfirmed])];
        await this.setNotification({
          msg: this.$t("dvir.completeAllItems"),
          type: "error",
          color: "red",
        });
        return;
      }
      if (!this.overallStatus) {
        await this.setNotification({
          msg: this.$t("dvir.statusRequired"),
          type: "error",
          color: "red",
        });
        return;
      }
      if (
        (this.requiresDriverSignature && !this.signatures.driver)
      ) {
        await this.setNotification({
          msg: this.$t("dvir.signatureRequired"),
          type: "error",
          color: "red",
        });
        return;
      }
      if (!this.record.id) {
        await this.setNotification({
          msg: this.$t("dvir.dvirSaveFailed"),
          type: "error",
          color: "red",
        });
        return;
      }
      try {
        this.setLoader(true);
        // Ok already uploaded each item's photo, but re-run in case one is still
        // pending (a photo added to an item that was confirmed earlier).
        for (let index = 0; index < this.items.length; index += 1) {
          await this.uploadPhotos(this.record.id, index);
        }
        await this.patchInspection(true);
        await this.setNotification({
          msg: this.$t("dvir.dvirSaved"),
          type: "success",
          color: "green",
        });
        this.$emit("submit", { unit: this.unit, template: this.form, id: this.record.id });
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    apiError(error) {
      if (typeof error === "string") return error;
      if (error?.error) return error.error;
      if (error?.detail) return error.detail.error || error.detail;
      const firstField = error && Object.values(error)[0];
      if (Array.isArray(firstField)) return firstField[0];
      return firstField || this.$t("dvir.dvirSaveFailed");
    },
  },
  created() {
    // The inspection prop already carries its items, so seed the answers before
    // the first render — loadInspection() rebuilds them once the fetch lands.
    this.resetAnswers();
    this.loadInspection();
  },
  beforeDestroy() {
    this.stopCamera();
    this.answers.forEach((answer) =>
      answer.photos.forEach((photo) => {
        if (photo.file) URL.revokeObjectURL(photo.url);
      })
    );
  },
};
</script>
<style scoped>
.inspection-card {
  background: #ffffff;
  border: 1px solid #e3e3e3;
  border-radius: 0.25rem;
}

/* Same card as the template editor's settings blocks — scoped styles don't
   cross components, so it has to be declared here too. */
.setting-card {
  padding: 1rem;
  text-align: left;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
}
.setting-card h4 {
  margin-bottom: 1rem;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #6c757d;
}
.inspection-card.is-open {
  border-color: rgba(45, 105, 246, 0.5);
}
.inspection-card.is-locked .inspection-head {
  cursor: not-allowed;
  opacity: 0.6;
}
.inspection-head {
  padding: 0.75rem 1rem;
  cursor: pointer;
}
.inspection-body {
  padding: 0 1rem 1rem;
}
.status-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
}
.status-dot.is-done {
  background-color: #2cce89;
  color: #ffffff;
}
.status-dot.is-current {
  background-color: rgba(45, 105, 246, 0.1);
  color: #2d69f6;
}
.status-dot.is-locked {
  background-color: #f4f4f4;
  color: #a9a9a9;
}
.unit-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: rgba(45, 105, 246, 0.1);
  border: 1px solid rgba(45, 105, 246, 0.5);
  color: #2d69f6;
  font-size: 12px;
  font-weight: 500;
}

/* Disabled controls don't emit clicks, so let them pass to the card wrapper
   that explains why the section is locked. */
.setting-card [disabled] {
  pointer-events: none;
}

/* ── ANSWER BUTTONS ──
   Same palette as the Cancel / Save pair: primary for Pass, secondary for Fail. */
.ans-btn {
  height: 40px;
  font-size: 14px;
  width: 100px;
}

/* Buttons share the row evenly on desktop and stack full width on mobile. */
.btn-split {
  display: flex;
  gap: 8px;
}
/* .btn-split .ans-btn {
  flex: 1 1 100%;
  min-width: 0;
}
@media (min-width: 768px) {
  .btn-split--half .ans-btn {
    flex: 1 1 calc(50% - 8px);
  }
  .btn-split--third .ans-btn {
    flex: 1 1 calc(33.333% - 8px);
  }
} */

/* ── PHOTOS ── */
.photo-upload {
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #2d69f6;
  border-radius: 4px;
  background-color: #2d69f6;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.photo-upload input {
  display: none;
}
.photo-preview {
  position: relative;
  width: 100%;
  max-width: 343px;
  height: 176px;
  overflow: hidden;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}
.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.photo-count {
  position: absolute;
  left: 8px;
  bottom: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}
.photo-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 50%;
  background-color: #fafafa;
  cursor: pointer;
}
.camera-frame {
  width: 100%;
  height: 320px;
  overflow: hidden;
  border-radius: 4px;
  background-color: #000000;
}
.camera-frame video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
